import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Shield, CheckCircle, Lock, AlertTriangle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import './KYCOnboardingModal.css';

const KYCOnboardingModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [kycData, setKycData] = useState(null);
  const [dismissCount, setDismissCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    checkAndShowModal();
  }, []);

  const checkAndShowModal = async () => {
    try {
      // Check if already dismissed in this session
      const sessionDismissed = sessionStorage.getItem('kycModalDismissed');
      if (sessionDismissed === 'true') {
        return;
      }

      // Get dismiss count from localStorage
      const storedDismissCount = parseInt(localStorage.getItem('kycModalDismissCount') || '0');
      setDismissCount(storedDismissCount);

      // Fetch KYC status
      const response = await axios.get('/api/kyc/status');
      if (response.data.success) {
        const kyc = response.data.kyc;
        setKycData(kyc);

        // Show modal if KYC is incomplete
        const shouldShow =
          kyc.status !== 'APPROVED' ||
          kyc.level !== 'LEVEL_3' ||
          ['NOT_STARTED', 'RESUBMISSION_REQUIRED'].includes(kyc.status);

        if (shouldShow) {
          // Make modal more urgent after multiple dismissals
          if (storedDismissCount < 5) {
            setIsOpen(true);
          } else if (storedDismissCount >= 5 && storedDismissCount < 10) {
            // Show on every 2nd login after 5 dismissals
            if (Math.random() > 0.5) {
              setIsOpen(true);
            }
          } else {
            // After 10 dismissals, always show
            setIsOpen(true);
          }
        }
      }
    } catch (err) {
      console.error('Failed to check KYC status:', err);
    }
  };

  const handleStartKYC = () => {
    setIsOpen(false);
    sessionStorage.setItem('kycModalDismissed', 'true');
    navigate('/kyc');
  };

  const handleDismiss = () => {
    const newCount = dismissCount + 1;
    setDismissCount(newCount);
    localStorage.setItem('kycModalDismissCount', newCount.toString());
    sessionStorage.setItem('kycModalDismissed', 'true');
    setIsOpen(false);
  };

  const getModalContent = () => {
    if (!kycData) return null;

    const urgency = dismissCount >= 5 ? 'high' : dismissCount >= 3 ? 'medium' : 'low';

    switch (kycData.status) {
      case 'NOT_STARTED':
        return {
          icon: <AlertTriangle size={48} className="modal-icon warning" />,
          title: urgency === 'high' ? 'Action Required: Complete KYC' : 'Complete Your KYC Verification',
          subtitle: urgency === 'high'
            ? 'Your account has limited functionality without KYC verification'
            : 'Unlock payments and start earning in just 5 minutes',
          benefits: [
            { icon: <CheckCircle size={20} />, text: 'Accept online payments from customers', highlight: true },
            { icon: <CheckCircle size={20} />, text: 'Withdraw earnings to your bank account', highlight: true },
            { icon: <CheckCircle size={20} />, text: 'Access advanced analytics and insights', highlight: false },
            { icon: <CheckCircle size={20} />, text: 'Get priority customer support', highlight: false }
          ],
          primaryAction: 'Start KYC Verification',
          warning: urgency === 'high' ? 'Without KYC, you cannot receive any payments!' : null
        };

      case 'IN_PROGRESS':
        return {
          icon: <Shield size={48} className="modal-icon progress" />,
          title: 'Continue Your KYC Verification',
          subtitle: `You're ${kycData.progressPercentage}% done - just a few more steps!`,
          benefits: [
            { icon: <CheckCircle size={20} />, text: `Complete ${100 - kycData.progressPercentage}% to start accepting payments`, highlight: true },
            { icon: <CheckCircle size={20} />, text: 'Takes only 3-5 minutes to complete', highlight: false },
            { icon: <CheckCircle size={20} />, text: 'Secure and encrypted process', highlight: false }
          ],
          primaryAction: 'Continue KYC',
          progress: kycData.progressPercentage
        };

      case 'RESUBMISSION_REQUIRED':
        return {
          icon: <AlertTriangle size={48} className="modal-icon error" />,
          title: 'KYC Resubmission Required',
          subtitle: kycData.rejectionReason || 'Please update your information',
          benefits: [
            { icon: <AlertTriangle size={20} />, text: 'Review the feedback from our team', highlight: true },
            { icon: <AlertTriangle size={20} />, text: 'Update required information', highlight: true },
            { icon: <CheckCircle size={20} />, text: 'Get approved within 24 hours', highlight: false }
          ],
          primaryAction: 'Update KYC Information',
          warning: 'Your account has limited access until KYC is approved'
        };

      default:
        return null;
    }
  };

  if (!isOpen || !kycData) return null;

  const content = getModalContent();
  if (!content) return null;

  const canDismiss = dismissCount < 3; // After 3 dismissals, user must complete or actively skip

  return (
    <div className="kyc-modal-overlay">
      <div className="kyc-modal-container">
        {canDismiss && (
          <button className="kyc-modal-close" onClick={handleDismiss} title="Remind me later">
            <X size={24} />
          </button>
        )}

        <div className="kyc-modal-content">
          <div className="kyc-modal-header">
            {content.icon}
            <h2 className="kyc-modal-title">{content.title}</h2>
            <p className="kyc-modal-subtitle">{content.subtitle}</p>
          </div>

          {content.progress !== undefined && (
            <div className="kyc-modal-progress">
              <div className="kyc-modal-progress-bar">
                <div
                  className="kyc-modal-progress-fill"
                  style={{ width: `${content.progress}%` }}
                />
              </div>
              <span className="kyc-modal-progress-text">{content.progress}% Complete</span>
            </div>
          )}

          {content.warning && (
            <div className="kyc-modal-warning">
              <AlertTriangle size={18} />
              <span>{content.warning}</span>
            </div>
          )}

          <div className="kyc-modal-benefits">
            {content.benefits.map((benefit, index) => (
              <div key={index} className={`kyc-modal-benefit ${benefit.highlight ? 'highlight' : ''}`}>
                <span className="benefit-icon-wrapper">{benefit.icon}</span>
                <span className="benefit-text">{benefit.text}</span>
              </div>
            ))}
          </div>

          <div className="kyc-modal-actions">
            <button className="kyc-modal-primary-btn" onClick={handleStartKYC}>
              {content.primaryAction}
              <ArrowRight size={20} />
            </button>
            {dismissCount < 3 && (
              <button className="kyc-modal-secondary-btn" onClick={handleDismiss}>
                Remind Me Later
              </button>
            )}
            {dismissCount >= 3 && (
              <button className="kyc-modal-text-btn" onClick={handleDismiss}>
                I understand the limitations - Skip for now
              </button>
            )}
          </div>

          {dismissCount >= 3 && (
            <p className="kyc-modal-footer-note">
              Note: You've postponed KYC {dismissCount} times. Complete it now to unlock full functionality.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCOnboardingModal;
